import React from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faHashtag,
  faArrowTrendUp,
  faChartBar,
  faChartLine,
  faChartPie,
  faTable,
  faFont,
  faImage,
} from "@fortawesome/free-solid-svg-icons"
import {
  TileType,
  DEFAULT_TILE_SIZES,
  createTile,
} from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  selectActivePage,
} from "features/Dashboard/runtime/dashboardStore"

/** MIME type of a palette drag; the value is the tile type. */
export const DRAG_TILE_TYPE = "application/x-dataq-tile"

const ITEMS: { type: TileType; label: string; icon: typeof faTable }[] = [
  { type: "kpi", label: "Number", icon: faHashtag },
  { type: "scorecard", label: "Scorecard", icon: faArrowTrendUp },
  { type: "bar", label: "Bar chart", icon: faChartBar },
  { type: "line", label: "Line chart", icon: faChartLine },
  { type: "pie", label: "Pie chart", icon: faChartPie },
  { type: "table", label: "Table", icon: faTable },
  { type: "text", label: "Text", icon: faFont },
  { type: "image", label: "Image", icon: faImage },
]

/**
 * The eight tile types as chips beside the page tabs. Click appends a tile
 * below the last one on the active page; drag drops one where it lands.
 */
const TilePalette: React.FC = () => {
  const page = useDashboardStore(selectActivePage)
  const addTile = useDashboardStore((s) => s.addTile)

  const append = (type: TileType): void => {
    if (!page) return
    const bottom = page.tiles.reduce(
      (m, t) => Math.max(m, t.layout.y + t.layout.h),
      0
    )
    addTile(
      page.id,
      createTile(type, { x: 0, y: bottom, ...DEFAULT_TILE_SIZES[type] })
    )
  }

  return (
    <div className="dq-palette" role="group" aria-label="Add tile">
      {ITEMS.map((item) => (
        <Tooltip
          key={item.type}
          title={`${item.label}: click to add, or drag onto the page`}
          placement="bottom"
        >
          <button
            type="button"
            className="dq-palette__chip"
            aria-label={`Add ${item.label}`}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_TILE_TYPE, item.type)
              e.dataTransfer.effectAllowed = "copy"
            }}
            onClick={() => append(item.type)}
          >
            <FontAwesomeIcon icon={item.icon} />
            <span className="dq-palette__label">{item.label}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  )
}

export default TilePalette
